/**
 * ProductContentTab V2
 *
 * Tab für Content Block Verwaltung.
 * Zeigt alle Content Blocks mit Editier- und Versioning-Funktionen.
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/api-client';
import { FileText, Plus, Clock, Pencil, Check, ChevronDown, Download, Grid3x3, Globe } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  useUpdateContentBlock,
  useCreateContentBlock,
  getBlockTypeInfo,
  formatContentForDisplay,
} from '@/hooks/useContentBlock';
import { getContentBlocksForScope } from '@/lib/api/content-blocks';
import { VersionHistoryOffcanvas } from '@/components/versioning';
import { ScheduleModal } from '@/components/publishing';
import { useActiveSchedule, useCancelSchedule } from '@/hooks/useActiveSchedule';
import { getProductChannelPackages, getProductActiveChannels, createChannelPackage } from '@/lib/api/channel-packages';
import { EID } from '@/lib/element-ids.registry';
import { pushDescriptionToJTL, toDescriptionBlockType } from '@/lib/api/descriptions';
import { ProductContentOverviewTab } from './content/ProductContentOverviewTab';
import { ProductContentChannelsTab } from './content/ProductContentChannelsTab';
import { AIFieldButton } from '@/components/v2/products/ai-field';
import type { ContentBlock, BlockType, ContentBlockContent } from '@/types/content-blocks';
import type { ChannelPackagesResponse, ChannelPackage } from '@/types/channel-packages';
import {
  Button,
  Badge,
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  Alert,
  AlertDescription,
  Textarea,
  Loading,
  EmptyState,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/v2/ui';

// Alle verfügbaren Block-Typen
const ALL_BLOCK_TYPES: BlockType[] = [
  'title',
  'description',
  'short_description',
  'bullets',
  'seo_meta',
];

interface ProductContentTabProps {
  productId: string;
  /** Optional: Callback für globalen Offcanvas (wenn vom Layout bereitgestellt) */
  onOpenVersionHistory?: (
    entityType: 'content_block' | 'channel_package' | 'product_asset_link',
    entityId: string,
    blockType?: BlockType
  ) => void;
}

export function ProductContentTab({ productId, onOpenVersionHistory }: ProductContentTabProps) {
  const { t } = useTranslation();
  // State - nur für lokales Offcanvas, wenn kein globaler Callback
  const [selectedBlock, setSelectedBlock] = useState<ContentBlock | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [scheduleVersionId, setScheduleVersionId] = useState<string | null>(null);
  const [scheduleVersionNumber, setScheduleVersionNumber] = useState<number>(0);

  // Sub-Tab State (Editor | Übersicht)
  const [activeSubTab, setActiveSubTab] = useState<'editor' | 'overview' | 'channels'>('editor');

  // Channel Selector State
  const [selectedChannelId, setSelectedChannelId] = useState<string>('product');
  const [isSyncing, setIsSyncing] = useState(false);

  // Bulk Edit Mode - when true, all blocks are opened in edit mode
  const [editAllMode, setEditAllMode] = useState(false);
  const shouldActivateEditMode = useRef(false);

  // Lock channel selector when coming from Overview (user already selected channel)
  const [channelSelectorLocked, setChannelSelectorLocked] = useState(false);

  // Handler for bulk edit from Overview tab
  const handleEditChannel = useCallback((channelId: string | null) => {
    setSelectedChannelId(channelId || 'product');
    setActiveSubTab('editor');
    setChannelSelectorLocked(true); // Lock channel selector
    shouldActivateEditMode.current = true;
  }, []);

  // Determine scope based on selected channel
  const scopeType = selectedChannelId === 'product' ? 'product' : 'channel_package';
  const scopeKey = selectedChannelId === 'product' ? productId : selectedChannelId;

  // Queries & Mutations - lädt Blocks basierend auf ausgewähltem Channel
  const contentBlocksQuery = useQuery({
    queryKey: ['content-blocks', scopeType, scopeKey],
    queryFn: () => getContentBlocksForScope(scopeType as 'product' | 'channel_package', scopeKey),
    enabled: !!productId && !!selectedChannelId,
    staleTime: 30_000,
  });

  const createMutation = useCreateContentBlock();

  // Channel Packages Query
  const channelPackagesQuery = useQuery<ChannelPackagesResponse>({
    queryKey: ['channelPackages', productId],
    queryFn: () => getProductChannelPackages(productId),
    retry: 2,
  });

  // Error handling für channel packages query
  useEffect(() => {
    if (channelPackagesQuery.isError) {
      console.error('Failed to load channel packages:', channelPackagesQuery.error);
      toast.error(t('content.channelPackagesLoadError'), {
        description: channelPackagesQuery.error instanceof Error
          ? channelPackagesQuery.error.message
          : t('errors.generic'),
      });
    }
  }, [channelPackagesQuery.isError, channelPackagesQuery.error, t]);

  // Get channel packages
  const channelPackages = useMemo(() => {
    const packages = channelPackagesQuery.data?.channel_packages || [];
    return packages;
  }, [channelPackagesQuery.data?.channel_packages]);

  // Active channels (inkl. solche ohne Texte) — für "neue Plattform anlegen"
  const activeChannelsQuery = useQuery({
    queryKey: ['product-active-channels', productId],
    queryFn: () => getProductActiveChannels(productId),
  });

  // Aktive Channels, die noch KEIN Channel-Package haben → erstmalig bespielbar
  const emptyChannels = useMemo(() => {
    const active = activeChannelsQuery.data?.channels || [];
    const existingChannelIds = new Set(channelPackages.map((p) => p.channel_id));
    return active.filter((ch) => ch.is_active && !existingChannelIds.has(ch.id));
  }, [activeChannelsQuery.data?.channels, channelPackages]);

  // State: läuft gerade ein on-the-fly Package-Create?
  const [isCreatingPackage, setIsCreatingPackage] = useState(false);

  // Channel-Auswahl: bei "new:<channelId>" erst Package anlegen, dann wechseln
  const handleChannelChange = useCallback(
    async (value: string) => {
      if (!value.startsWith('new:')) {
        setSelectedChannelId(value);
        return;
      }
      const channelId = value.slice('new:'.length);
      setIsCreatingPackage(true);
      try {
        toast.info(t('toast.info.channelPackageCreating'));
        const result = await createChannelPackage(productId, channelId);
        if (!result.success) {
          throw new Error(result.error || t('errors.generic'));
        }
        toast.success(t('toast.success.packageCreated'));
        await channelPackagesQuery.refetch();
        setSelectedChannelId(result.channel_package.id);
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : t('errors.generic')
        );
      } finally {
        setIsCreatingPackage(false);
      }
    },
    [productId, channelPackagesQuery, t]
  );

  // Sort blocks by type
  const sortedBlocks = useMemo(() => {
    const blocks = contentBlocksQuery.data?.content_blocks || [];
    return [...blocks].sort((a, b) => {
      const aIndex = ALL_BLOCK_TYPES.indexOf(a.block_type);
      const bIndex = ALL_BLOCK_TYPES.indexOf(b.block_type);
      return aIndex - bIndex;
    });
  }, [contentBlocksQuery.data?.content_blocks]);

  // Activate edit mode after data is loaded (for bulk edit from Overview)
  useEffect(() => {
    if (shouldActivateEditMode.current && !contentBlocksQuery.isLoading && sortedBlocks.length > 0) {
      shouldActivateEditMode.current = false; // Reset flag
      setEditAllMode(true);
      // Reset after blocks have started editing
      const timer = setTimeout(() => {
        setEditAllMode(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [contentBlocksQuery.isLoading, sortedBlocks.length]);

  // Berechne fehlende Block-Typen
  const existingTypes = useMemo(
    () => new Set(sortedBlocks.map((b) => b.block_type)),
    [sortedBlocks]
  );

  const missingTypes = useMemo(
    () => ALL_BLOCK_TYPES.filter((t) => !existingTypes.has(t)),
    [existingTypes]
  );

  // Handlers
  const handleOpenHistory = useCallback(
    (block: ContentBlock) => {
      // Wenn globaler Callback vorhanden, diesen nutzen
      if (onOpenVersionHistory) {
        onOpenVersionHistory('content_block', block.id, block.block_type);
      } else {
        // Sonst lokales Offcanvas öffnen
        setSelectedBlock(block);
        setIsHistoryOpen(true);
      }
    },
    [onOpenVersionHistory]
  );

  const handleCloseHistory = useCallback(() => {
    setIsHistoryOpen(false);
    setSelectedBlock(null);
  }, []);

  const handleScheduleVersion = useCallback(
    (versionId: string) => {
      const version = selectedBlock?.current_version;
      if (version) {
        setScheduleVersionId(versionId);
        setScheduleVersionNumber(version.version_number);
        setIsScheduleOpen(true);
      }
    },
    [selectedBlock]
  );

  const handleCloseSchedule = useCallback(() => {
    setIsScheduleOpen(false);
    setScheduleVersionId(null);
  }, []);

  // Handler für neuen Block erstellen
  const handleCreateBlock = useCallback(
    (blockType: BlockType) => {
      // Default-Content basierend auf Block-Typ
      let defaultContent: ContentBlockContent;
      switch (blockType) {
        case 'bullets':
          defaultContent = [];
          break;
        case 'seo_meta':
          defaultContent = { title: '', description: '' };
          break;
        default:
          defaultContent = '';
      }

      createMutation.mutate({
        scope_type: selectedChannelId === 'product' ? 'product' : 'channel_package',
        scope_key: selectedChannelId === 'product' ? productId : selectedChannelId,
        block_type: blockType,
        content: defaultContent,
      });
    },
    [createMutation, productId, selectedChannelId]
  );

  // Sync Handler for JTL Descriptions (MUST be before early returns!)
  const handleSyncFromJTL = useCallback(async () => {
    setIsSyncing(true);
    try {
      const { data: result } = await apiClient.post<{
        synced_count: number;
        created_blocks: number;
        updated_blocks: number;
        errors?: string[];
      }>(`/api/v1/products/${productId}/descriptions/sync`, {});

      if (result.synced_count > 0) {
        toast.success(t('toast.success.jtlSyncSuccess'), {
          description: t('content.syncDescCount', {
            count: result.synced_count,
            suffix: result.synced_count !== 1 ? 'en' : '',
            created: result.created_blocks,
            updated: result.updated_blocks,
          }),
        });
      } else {
        toast.info(t('content.noDescriptionsFound'), {
          description: t('content.fromJtl'),
        });
      }

      if (result.errors && result.errors.length > 0) {
        toast.warning(t('toast.success.syncPartial'), {
          description: t('content.syncErrorsCount', { count: result.errors.length }),
        });
      }

      contentBlocksQuery.refetch();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : (error as { message?: string })?.message || t('errors.syncing');
      toast.error(t('toast.error.jtlSync'), {
        description: errorMessage,
      });
      console.error('JTL Sync error:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [productId, contentBlocksQuery, t]);

  // Get selected channel package details
  const selectedPackage = channelPackages.find((pkg: ChannelPackage) => pkg.id === selectedChannelId);
  const isJTLChannel = selectedPackage?.channel?.workspace_integrations?.integration_type === 'jtl';

  console.log('[ProductContentTab] Channel detection:', {
    selectedChannelId,
    channelPackagesCount: channelPackages.length,
    channelPackageIds: channelPackages.map((p: ChannelPackage) => p.id),
    selectedPackage: selectedPackage ? 'found' : 'NOT FOUND',
    integrationType: selectedPackage?.channel?.workspace_integrations?.integration_type,
    isJTLChannel
  });

  return (
    <div className="product-content-tab">
      <Tabs value={activeSubTab} onValueChange={(value) => setActiveSubTab(value as 'editor' | 'overview' | 'channels')}>
        {/* Sub-Tab Navigation */}
        <TabsList className="mb-6">
          <TabsTrigger value="editor" className="flex items-center gap-2">
            <Pencil className="h-4 w-4" />
            Editor
          </TabsTrigger>
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Grid3x3 className="h-4 w-4" />
            {t('common.overview')}
          </TabsTrigger>
          <TabsTrigger value="channels" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            {t('productContent.channels.title')}
          </TabsTrigger>
        </TabsList>

        {/* Editor Sub-Tab */}
        <TabsContent value="editor" className="space-y-4">
          {/* Channel Selector & Sync Button */}
          <div className="flex items-center gap-3 pb-2 border-b border-neutral-200 dark:border-neutral-800">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {t('content.contentFor')}
          </label>
          {channelSelectorLocked ? (
            <div className="px-3 py-2 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
              <span className="text-sm font-medium text-neutral-900 dark:text-neutral-50">
                {selectedChannelId === 'product'
                  ? t('content.productGlobal')
                  : selectedPackage?.channel?.name || 'Channel'}
              </span>
              {selectedPackage && (
                <Badge variant="secondary" className="text-xs ml-2">
                  {selectedPackage.channel?.workspace_integrations?.integration_type || 'unknown'}
                </Badge>
              )}
            </div>
          ) : (
            <SelectRoot value={selectedChannelId} onValueChange={handleChannelChange}>
              <SelectTrigger
                id={EID['content-channel-select']}
                className="content-channel-select w-48"
                aria-label={t('content.contentFor')}
                aria-busy={isCreatingPackage}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="product">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span>{t('content.productGlobal')}</span>
                  </div>
                </SelectItem>
                {channelPackagesQuery.isLoading && (
                  <SelectItem value="loading" disabled>
                    <span className="text-neutral-400">{t('products.channelsLoading')}</span>
                  </SelectItem>
                )}
                {channelPackagesQuery.isError && (
                  <SelectItem value="error" disabled>
                    <span className="text-red-600 dark:text-red-400">{t('errors.loading')}</span>
                  </SelectItem>
                )}
                {channelPackages.map((pkg: ChannelPackage) => (
                  <SelectItem key={pkg.id} value={pkg.id}>
                    <div className="flex items-center gap-2">
                      <span>{pkg.channel?.name || t('content.unknownChannel')}</span>
                      <Badge variant="secondary" className="text-xs">
                        {pkg.channel?.workspace_integrations?.integration_type || 'unknown'}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
                {emptyChannels.map((channel) => (
                  <SelectItem key={`new:${channel.id}`} value={`new:${channel.id}`}>
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-neutral-400" />
                      <span>
                        {channel.channel_name || channel.channel_display_name || t('content.unknownChannel')}
                      </span>
                      <Badge variant="primary" className="text-xs">
                        {t('content.channelNew')}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          )}
        </div>

        {/* Sync from JTL Button - only show for JTL channels */}
        {selectedChannelId !== 'product' && isJTLChannel && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleSyncFromJTL}
            disabled={isSyncing}
            title={t('content.syncFromJtlTitle')}
          >
            {isSyncing ? (
              <Loading size="sm" />
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                {t('content.syncFromJtl')}
              </>
            )}
          </Button>
        )}

        {/* AI Field Button — master product description (no channelId = product scope) */}
        {selectedChannelId === 'product' && (
          <AIFieldButton productId={productId} fieldType="product_description" />
        )}
      </div>

      {/* Loading State */}
      {contentBlocksQuery.isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loading size="lg" label={t('common.loading')} />
        </div>
      )}

      {/* Error State */}
      {contentBlocksQuery.isError && (
        <Alert variant="error">
          <AlertDescription>{t('errors.loading')}</AlertDescription>
        </Alert>
      )}

      {/* Empty State */}
      {!contentBlocksQuery.isLoading && !contentBlocksQuery.isError && sortedBlocks.length === 0 && (
        <EmptyState
          icon={<FileText className="h-12 w-12" />}
          title={t('content.noContentBlocksTitle')}
          description={t('products.noContentBlocks')}
          action={
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="primary" disabled={createMutation.isPending}>
                  {createMutation.isPending ? (
                    <Loading size="sm" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  {t('products.addContentBlock')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {ALL_BLOCK_TYPES.map((type) => {
                  const info = getBlockTypeInfo(type);
                  return (
                    <DropdownMenuItem key={type} onClick={() => handleCreateBlock(type)}>
                      {info.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          }
        />
      )}

      {/* Content List - only show when data loaded and not empty */}
      {!contentBlocksQuery.isLoading && !contentBlocksQuery.isError && sortedBlocks.length > 0 && (
        <>
      {/* Header mit Hinzufügen-Button */}
      <div className="flex justify-between items-center">
        <div>
          <h5 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">Content Blocks</h5>
          <span className="text-sm text-neutral-600 dark:text-neutral-400">
            {t('products.blocksCount', { count: sortedBlocks.length, total: ALL_BLOCK_TYPES.length })}
            {selectedChannelId !== 'product' && (
              <Badge variant="secondary" className="ml-2">Channel-specific</Badge>
            )}
          </span>
        </div>

        {/* Hinzufügen-Dropdown - nur wenn noch Typen fehlen */}
        {missingTypes.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <Loading size="sm" />
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    {t('common.add')}
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {missingTypes.map((type) => {
                const info = getBlockTypeInfo(type);
                return (
                  <DropdownMenuItem key={type} onClick={() => handleCreateBlock(type)}>
                    <div className="flex flex-col">
                      <span className="font-medium">{info.label}</span>
                      <span className="text-xs text-neutral-600 dark:text-neutral-400">
                        {info.description}
                      </span>
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Block List */}
      <div className="content-blocks-list space-y-3">
        {sortedBlocks.map((block) => (
          <ContentBlockCard
            key={block.id}
            block={block}
            onOpenHistory={() => handleOpenHistory(block)}
            startInEditMode={editAllMode}
            productId={productId}
            channelPackageId={selectedChannelId !== 'product' ? selectedChannelId : null}
            isJTLChannel={isJTLChannel}
          />
        ))}
      </div>
        </>
      )}

      {/* Version History Offcanvas - nur wenn kein globaler Callback */}
      {!onOpenVersionHistory && selectedBlock && (
        <VersionHistoryOffcanvas
          entityType="content_block"
          entityId={selectedBlock.id}
          blockType={selectedBlock.block_type}
          isOpen={isHistoryOpen}
          onClose={handleCloseHistory}
          onScheduleVersion={handleScheduleVersion}
        />
      )}

      {/* Schedule Modal */}
      {selectedBlock && scheduleVersionId && (
        <ScheduleModal
          isOpen={isScheduleOpen}
          onClose={handleCloseSchedule}
          entityType="content_block"
          entityId={selectedBlock.id}
          versionId={scheduleVersionId}
          versionNumber={scheduleVersionNumber}
          currentVersionNumber={selectedBlock.current_version?.version_number}
        />
      )}
        </TabsContent>

        {/* Overview Sub-Tab */}
        <TabsContent value="overview">
          <ProductContentOverviewTab productId={productId} onEditChannel={handleEditChannel} />
        </TabsContent>

        {/* Channels Sub-Tab (JTL Live Descriptions) */}
        <TabsContent value="channels">
          <ProductContentChannelsTab productId={productId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Helper: Extract available languages from content
 */
function extractLanguages(content: unknown): string[] {
  if (!content || typeof content !== 'object' || Array.isArray(content)) {
    return [];
  }

  const obj = content as Record<string, unknown>;
  const commonLangs = ['de', 'en', 'fr', 'es', 'it', 'pl', 'ger', 'eng', 'fre', 'spa', 'ita', 'pol'];

  return Object.keys(obj).filter(key =>
    commonLangs.includes(key.toLowerCase()) && typeof obj[key] === 'string'
  );
}

/**
 * Helper: Check if content is multi-lingual
 */
function isMultiLingualContent(content: unknown, blockType: BlockType): boolean {
  if (blockType === 'bullets') return false; // bullets are arrays
  if (blockType === 'seo_meta') {
    // SEO can be {"de": {"title": "...", "description": "..."}}
    if (!content || typeof content !== 'object') return false;
    const langs = extractLanguages(content);
    return langs.length > 0;
  }

  // title, description, short_description
  const langs = extractLanguages(content);
  return langs.length > 0;
}

/**
 * Single Content Block Card
 */
interface ContentBlockCardProps {
  block: ContentBlock;
  onOpenHistory: () => void;
  startInEditMode?: boolean;
  productId: string;
  channelPackageId: string | null; // null = product-level
  isJTLChannel: boolean;
}

function ContentBlockCard({
  block,
  onOpenHistory,
  startInEditMode = false,
  productId,
  channelPackageId,
  isJTLChannel
}: ContentBlockCardProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(startInEditMode);
  const [editValue, setEditValue] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('de');
  const [languageContents, setLanguageContents] = useState<Record<string, string>>({});
  const [showSaveDropdown, setShowSaveDropdown] = useState(false);
  const [isPushingToJTL, setIsPushingToJTL] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const typeInfo = getBlockTypeInfo(block.block_type);
  const updateMutation = useUpdateContentBlock(block.id);

  // Check if content is multi-lingual
  const content = block.current_version?.content;
  const isMultiLingual = isMultiLingualContent(content, block.block_type);
  const availableLanguages = useMemo(() => isMultiLingual ? extractLanguages(content) : [], [isMultiLingual, content]);

  // Click-Outside Handler für Dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSaveDropdown(false);
      }
    };

    if (showSaveDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSaveDropdown]);

  // Active schedule check
  const activeScheduleQuery = useActiveSchedule({
    entityType: 'content_block',
    entityId: block.id,
  });

  const cancelScheduleMutation = useCancelSchedule('content_block', block.id);

  const handleStartEdit = useCallback(() => {
    const content = block.current_version?.content;

    if (isMultiLingual) {
      // Multi-lingual: Extract content per language
      const langContents: Record<string, string> = {};
      const obj = content as unknown as Record<string, unknown>;

      availableLanguages.forEach(lang => {
        if (block.block_type === 'seo_meta' && typeof obj[lang] === 'object') {
          langContents[lang] = JSON.stringify(obj[lang], null, 2);
        } else if (typeof obj[lang] === 'string') {
          langContents[lang] = obj[lang] as string;
        } else {
          langContents[lang] = '';
        }
      });

      setLanguageContents(langContents);
      setSelectedLanguage(availableLanguages[0] || 'de');
    } else {
      // Single-language or old format
      if (block.block_type === 'bullets' && Array.isArray(content)) {
        setEditValue(content.join('\n'));
      } else if (typeof content === 'object') {
        setEditValue(JSON.stringify(content, null, 2));
      } else {
        setEditValue(String(content || ''));
      }
    }
    setIsEditing(true);
  }, [block, isMultiLingual, availableLanguages]);

  // Auto-start edit mode if requested
  useEffect(() => {
    if (startInEditMode && !isEditing) {
      handleStartEdit();
    }
  }, [startInEditMode, handleStartEdit, isEditing]);

  const handleSave = async (publish: boolean = true) => {
    console.log('[ContentBlockCard] handleSave called:', {
      publish,
      isJTLChannel,
      channelPackageId,
      productId,
      blockType: block.block_type
    });

    let parsedContent: ContentBlockContent;

    // 1. Prepare content
    if (isMultiLingual) {
      // Multi-lingual: Rebuild structure
      const obj: Record<string, unknown> = {};

      for (const [lang, value] of Object.entries(languageContents)) {
        if (block.block_type === 'seo_meta') {
          try {
            obj[lang] = JSON.parse(value);
          } catch {
            toast.error(t('errors.invalidJsonForLang', { lang: lang.toUpperCase() }));
            return;
          }
        } else {
          obj[lang] = value;
        }
      }

      parsedContent = obj as unknown as ContentBlockContent;
    } else {
      // Single-language or old format
      if (block.block_type === 'bullets') {
        parsedContent = editValue.split('\n').filter(Boolean);
      } else if (block.block_type === 'seo_meta') {
        try {
          parsedContent = JSON.parse(editValue);
        } catch {
          toast.error(t('errors.invalidJson'));
          return;
        }
      } else {
        parsedContent = editValue;
      }
    }

    // 2. If publishing to JTL channel → Push to JTL first
    console.log('[ContentBlockCard] Checking JTL push conditions:', {
      publish,
      isJTLChannel,
      channelPackageId,
      shouldPush: publish && isJTLChannel && channelPackageId
    });

    if (publish && isJTLChannel && channelPackageId) {
      console.log('[ContentBlockCard] Starting JTL push...');
      setIsPushingToJTL(true);

      try {
        // Push for each language
        const languages = isMultiLingual
          ? Object.keys(languageContents)
          : [selectedLanguage || 'de'];

        console.log('[ContentBlockCard] Pushing languages:', languages);

        // block_type is constant across languages. 'bullets' and other
        // non-description types are NOT pushable via this generic path
        // (bullets → Custom-Field slots, handled elsewhere). Fail loud
        // instead of sending an invalid block_type the backend rejects.
        const jtlBlockType = toDescriptionBlockType(block.block_type);
        if (!jtlBlockType) {
          throw new Error(t('content.jtlPushFailed', { status: block.block_type }));
        }

        for (const lang of languages) {
          console.log('[ContentBlockCard] Pushing language:', lang);

          // Prepare content for this language
          const contentForLanguage = isMultiLingual
            ? languageContents[lang]
            : parsedContent;

          const result = await pushDescriptionToJTL(productId, {
            sales_channel_id: channelPackageId,
            language_iso: lang,
            block_type: jtlBlockType,
            content: contentForLanguage as string,
          });

          if (!result.success) {
            throw new Error(result.error || t('content.jtlPushFailed', { status: 'error' }));
          }
        }

        toast.success(t('toast.success.jtlPushSuccess'));
        // Invalidate channels tab so it shows updated JTL data
        queryClient.invalidateQueries({ queryKey: ['channel-descriptions', productId] });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : t('errors.generic');
        toast.error(t('toast.error.jtlPush'), {
          description: errorMessage,
        });
        setIsPushingToJTL(false);
        // Don't save/publish if JTL push failed
        return;
      } finally {
        setIsPushingToJTL(false);
      }
    }

    // 3. Save/publish content block
    updateMutation.mutate(
      { content: parsedContent, publish },
      {
        onSuccess: () => setIsEditing(false),
      }
    );
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleCancelSchedule = () => {
    const schedule = activeScheduleQuery.data?.schedule;
    if (schedule) {
      cancelScheduleMutation.mutate(schedule.id);
    }
  };

  const displayContent = formatContentForDisplay(block.current_version?.content, block.block_type);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-neutral-900 dark:text-neutral-50">{typeInfo.label}</span>
          {block.current_version && (
            <Badge variant="secondary">V{block.current_version.version_number}</Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onOpenHistory} title={t('content.showVersions')}>
            <Clock className="h-4 w-4" />
          </Button>
          {!isEditing && (
            <Button variant="outline" size="sm" onClick={handleStartEdit} title={t('content.editBlock')}>
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {/* Active Schedule Banner (Compact) */}
        {activeScheduleQuery.data?.schedule && (
          <Alert variant="warning" className="mb-3">
            <Clock className="h-4 w-4" />
            <div className="flex-1">
              <AlertDescription>
                <div className="flex items-center justify-between">
                  <span className="text-sm">{t('products.temporaryVersionActive')}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelSchedule}
                    disabled={cancelScheduleMutation.isPending}
                    className="ml-4"
                  >
                    {cancelScheduleMutation.isPending ? <Loading size="sm" /> : t('common.cancel')}
                  </Button>
                </div>
              </AlertDescription>
            </div>
          </Alert>
        )}

        {/* View Mode */}
        {!isEditing && (
          <div className="content-preview">
            {isMultiLingual && availableLanguages.length > 1 ? (
              <Tabs value={selectedLanguage} onValueChange={setSelectedLanguage}>
                <TabsList className="mb-3">
                  {availableLanguages.map(lang => (
                    <TabsTrigger key={lang} value={lang}>
                      {lang.toUpperCase()}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {availableLanguages.map(lang => {
                  const obj = content as unknown as Record<string, unknown>;
                  let langContent = '';

                  if (block.block_type === 'seo_meta' && typeof obj[lang] === 'object') {
                    const meta = obj[lang] as { title?: string; description?: string };
                    langContent = t('content.seoMetaDisplay', { title: meta.title || '', description: meta.description || '' });
                  } else if (typeof obj[lang] === 'string') {
                    langContent = obj[lang] as string;
                  }

                  return (
                    <TabsContent key={lang} value={lang}>
                      {langContent ? (
                        <pre className="whitespace-pre-wrap text-sm text-neutral-900 dark:text-neutral-50 font-sans">
                          {langContent}
                        </pre>
                      ) : (
                        <span className="text-neutral-600 dark:text-neutral-400 italic">{t('content.noContentForLang', { lang: lang.toUpperCase() })}</span>
                      )}
                    </TabsContent>
                  );
                })}
              </Tabs>
            ) : (
              <>
                {displayContent ? (
                  <pre className="whitespace-pre-wrap text-sm text-neutral-900 dark:text-neutral-50 font-sans">
                    {displayContent}
                  </pre>
                ) : (
                  <span className="text-neutral-600 dark:text-neutral-400 italic">{t('content.noContent')}</span>
                )}
              </>
            )}
          </div>
        )}

        {/* Edit Mode */}
        {isEditing && (
          <div className="content-edit space-y-3">
            {isMultiLingual ? (
              <Tabs value={selectedLanguage} onValueChange={setSelectedLanguage}>
                <div className="flex items-center justify-between mb-3">
                  <TabsList>
                    {availableLanguages.map(lang => (
                      <TabsTrigger key={lang} value={lang}>
                        {lang.toUpperCase()}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newLang = prompt(t('content.addLanguagePrompt'));
                      if (newLang && !availableLanguages.includes(newLang)) {
                        setLanguageContents(prev => ({ ...prev, [newLang]: '' }));
                        setSelectedLanguage(newLang);
                      }
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {t('content.addLanguage')}
                  </Button>
                </div>
                {Object.keys(languageContents).map(lang => (
                  <TabsContent key={lang} value={lang}>
                    <Textarea
                      rows={block.block_type === 'description' ? 8 : 4}
                      value={languageContents[lang] || ''}
                      onChange={(e) => setLanguageContents(prev => ({ ...prev, [lang]: e.target.value }))}
                      placeholder={`${typeInfo.description} (${lang.toUpperCase()})`}
                    />
                  </TabsContent>
                ))}
              </Tabs>
            ) : (
              <Textarea
                rows={block.block_type === 'description' ? 8 : 4}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                placeholder={typeInfo.description}
                className="font-mono text-sm"
              />
            )}
            <div className="flex gap-2">
              {/* Split-Button: Veröffentlichen (Standard) + Entwurf speichern */}
              <div className="relative" ref={dropdownRef}>
                <div className="flex">
                  <Button
                    variant="primary"
                    onClick={() => handleSave(true)}
                    disabled={updateMutation.isPending || isPushingToJTL}
                    className="rounded-r-none"
                  >
                    {(updateMutation.isPending || isPushingToJTL) ? (
                      <Loading size="sm" />
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        {t('common.publish')}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => setShowSaveDropdown(!showSaveDropdown)}
                    disabled={updateMutation.isPending || isPushingToJTL}
                    className="rounded-l-none border-l border-l-primary-700 px-2"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </div>
                {showSaveDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-48 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-lg z-10">
                    <button
                      className="w-full text-left px-4 py-2 text-sm text-neutral-900 dark:text-neutral-50 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                      onClick={() => {
                        setShowSaveDropdown(false);
                        handleSave(false);
                      }}
                    >
                      {t('content.saveDraft')}
                    </button>
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                onClick={handleCancelEdit}
                disabled={updateMutation.isPending || isPushingToJTL}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {/* Footer with meta info */}
      {block.current_version && (
        <CardFooter className="text-sm text-neutral-600 dark:text-neutral-400 flex justify-between pt-3">
          <span>{block.current_version.changed_by_name || 'System'}</span>
          <span>{new Date(block.current_version.created_at).toLocaleString(i18n.language)}</span>
        </CardFooter>
      )}
    </Card>
  );
}
